import { ReportActionLogEntryResponse } from "../../../../client";
import { formatTimestamp } from "../../utils";

export type ActionType = ReportActionLogEntryResponse["action_type"];

export const ACTION_TYPE_LABELS: Record<ActionType, string> = {
  EMAIL_RECEIVED: "Email Received",
  REPORT_CREATED: "Report Created",
  EMAIL_ADDED_TO_REPORT: "Email Added to Report",
  EMAIL_REJECTED_INVALID_SENDER: "Email Rejected (Invalid Sender)",
  EMAIL_REJECTED_DUPLICATE: "Email Rejected (Duplicate)",
  EMAIL_REJECTED_NO_ATTACHMENTS: "Email Rejected (No Attachments)",
  EMAIL_REJECTED_REPLY_THREAD: "Email Rejected (Reply Thread)",
  UPDATE_REPORT: "Report Updated",
  STATUS_CHANGED: "Status Changed",
  ASSIGNEE_CHANGED: "Assignee Changed",
  UPDATE_ADDRESSES: "Addresses Updated",
  INTEGRATION_JOB_ENQUEUED: "Integration Job Enqueued",
  INTEGRATION_JOB_STARTED: "Integration Job Started",
  INTEGRATION_JOB_COMPLETED: "Integration Job Completed",
  INTEGRATION_JOB_FAILED: "Integration Job Failed",
  CLEARANCE_EMAIL_SENT: "Clearance Email Sent",
  QUOTE_INDICATION_DRAFTED: "Quote Indication Drafted",
  ATTACHMENT_UPLOADED: "Attachment Uploaded",
  ATTACHMENT_DELETED: "Attachment Deleted",

  COMMENT_ADDED: "Comment Added",
  COMMENT_EDITED: "Comment Edited",
  COMMENT_DELETED: "Comment Deleted",

  BOR_CREATED: "BOR Created",
  BOR_FINALIZED: "BOR Finalized",
  BOR_RESCINDED: "BOR Rescinded",
};

export const ACTION_TYPE_ICONS: Record<ActionType, string> = {
  EMAIL_RECEIVED: "pi pi-envelope",
  REPORT_CREATED: "pi pi-file",
  EMAIL_ADDED_TO_REPORT: "pi pi-link",
  EMAIL_REJECTED_INVALID_SENDER: "pi pi-ban",
  EMAIL_REJECTED_DUPLICATE: "pi pi-copy",
  EMAIL_REJECTED_NO_ATTACHMENTS: "pi pi-file-excel",
  EMAIL_REJECTED_REPLY_THREAD: "pi pi-reply",
  UPDATE_REPORT: "pi pi-pencil",
  STATUS_CHANGED: "pi pi-flag",
  ASSIGNEE_CHANGED: "pi pi-user",
  UPDATE_ADDRESSES: "pi pi-map-marker",
  INTEGRATION_JOB_ENQUEUED: "pi pi-clock",
  INTEGRATION_JOB_STARTED: "pi pi-spinner",
  INTEGRATION_JOB_COMPLETED: "pi pi-check-circle",
  INTEGRATION_JOB_FAILED: "pi pi-times-circle",
  CLEARANCE_EMAIL_SENT: "pi pi-send",
  QUOTE_INDICATION_DRAFTED: "pi pi-file-edit",
  ATTACHMENT_UPLOADED: "pi pi-paperclip",
  ATTACHMENT_DELETED: "pi pi-trash",

  COMMENT_ADDED: "pi pi-comment",
  COMMENT_EDITED: "pi pi-pencil",
  COMMENT_DELETED: "pi pi-trash",

  BOR_CREATED: "pi pi-arrows-h",
  BOR_FINALIZED: "pi pi-check-circle",
  BOR_RESCINDED: "pi pi-undo",
};

type TagSeverity = "success" | "info" | "warning" | "danger" | null | undefined;

export const ACTION_TYPE_SEVERITY: Record<ActionType, TagSeverity> = {
  EMAIL_RECEIVED: "info",
  REPORT_CREATED: "success",
  EMAIL_ADDED_TO_REPORT: "info",
  EMAIL_REJECTED_INVALID_SENDER: "danger",
  EMAIL_REJECTED_DUPLICATE: "warning",
  EMAIL_REJECTED_NO_ATTACHMENTS: "warning",
  EMAIL_REJECTED_REPLY_THREAD: "warning",
  UPDATE_REPORT: "info",
  STATUS_CHANGED: "info",
  ASSIGNEE_CHANGED: "info",
  UPDATE_ADDRESSES: "info",
  INTEGRATION_JOB_ENQUEUED: null,
  INTEGRATION_JOB_STARTED: "info",
  INTEGRATION_JOB_COMPLETED: "success",
  INTEGRATION_JOB_FAILED: "danger",
  CLEARANCE_EMAIL_SENT: "success",
  QUOTE_INDICATION_DRAFTED: "info",
  ATTACHMENT_UPLOADED: "info",
  ATTACHMENT_DELETED: "warning",

  COMMENT_ADDED: "info",
  COMMENT_EDITED: "info",
  COMMENT_DELETED: "warning",

  BOR_CREATED: "warning",
  BOR_FINALIZED: "success",
  BOR_RESCINDED: "info",
};

export { formatTimestamp };

export const formatLabel = (actionType: ActionType) =>
  ACTION_TYPE_LABELS[actionType] ?? actionType;

export const formatMetadata = (
  metadata: Record<string, unknown> | null
): string => {
  if (!metadata) return "";
  const parts: string[] = [];
  if (metadata.integration_type) {
    parts.push(`Type: ${metadata.integration_type}`);
  }
  if (metadata.updated_fields && Array.isArray(metadata.updated_fields)) {
    parts.push(`Fields: ${(metadata.updated_fields as string[]).join(", ")}`);
  }
  if (metadata.address_count) {
    parts.push(`Addresses: ${metadata.address_count}`);
  }
  if (metadata.comment_preview) {
    parts.push(`"${(metadata.comment_preview as string).slice(0, 60)}..."`);
  }
  if (metadata.email_subject) {
    parts.push(`Subject: ${metadata.email_subject}`);
  }
  if (metadata.sender) {
    parts.push(`From: ${metadata.sender}`);
  }
  if (metadata.reason) {
    parts.push(`Reason: ${metadata.reason}`);
  }
  if (metadata.error_code) {
    parts.push(`Error: ${metadata.error_code}`);
  }
  if (metadata.status) {
    parts.push(`Status: ${metadata.status}`);
  }
  if (metadata.recipients && Array.isArray(metadata.recipients)) {
    parts.push(`Recipients: ${(metadata.recipients as string[]).join(", ")}`);
  }
  if (metadata.filenames && Array.isArray(metadata.filenames)) {
    parts.push(`Files: ${(metadata.filenames as string[]).join(", ")}`);
  }
  if (metadata.classification) {
    parts.push(`Classification: ${metadata.classification}`);
  }
  if (metadata.source) {
    parts.push(`Source: ${metadata.source}`);
  }
  if (metadata.draft_url) {
    parts.push(`Draft URL: ${metadata.draft_url}`);
  }
  return parts.join(" | ");
};

// Job start/result entries carry a per-task job_ref_id and so get the
// expandable group view (with log links) — always, even as a single row.
const LINKABLE_JOB_ACTION_TYPES: ReadonlySet<ActionType> = new Set([
  "INTEGRATION_JOB_STARTED",
  "INTEGRATION_JOB_COMPLETED",
  "INTEGRATION_JOB_FAILED",
]);

/**
 * A single (ungrouped) action log row.
 */
export type TimelineSingle = {
  kind: "single";
  entry: ReportActionLogEntryResponse;
  anchor: string;
};

/**
 * A collapsed set of integration-job rows sharing the same action type,
 * integration type, and batch. `start`/`end` bound the rows' timestamps;
 * `anchor` (the start) is the group's position in the chronological timeline.
 */
export type TimelineGroup = {
  kind: "group";
  key: string;
  actionType: ActionType;
  integrationType: string;
  entries: ReportActionLogEntryResponse[];
  start: string;
  end: string;
  anchor: string;
};

export type TimelineItem = TimelineSingle | TimelineGroup;

const groupKeyFor = (entry: ReportActionLogEntryResponse): string | null => {
  if (!LINKABLE_JOB_ACTION_TYPES.has(entry.action_type)) return null;
  const metadata = entry.metadata;
  const integrationType =
    metadata && typeof metadata.integration_type === "string"
      ? metadata.integration_type
      : "unknown";
  const batchId =
    metadata && typeof metadata.batch_id === "string"
      ? metadata.batch_id
      : null;
  // Batched entries collapse together; batchless ones (e.g. standalone jobs)
  // each form their own single-row group so they still get the link expansion.
  return batchId
    ? `${entry.action_type}::${integrationType}::${batchId}`
    : `${entry.action_type}::${entry.id}`;
};

/**
 * Collapse job start/result rows that share (action type, integration type,
 * batch id) into a single expandable group, leaving every other row on its own.
 * Such rows always become a group (even a one-row one, so the log link is
 * reachable). Groups sit at their earliest member's timestamp; the returned
 * list is sorted chronologically ascending.
 */
export const buildTimelineItems = (
  actions: ReportActionLogEntryResponse[]
): TimelineItem[] => {
  const membersByKey = new Map<string, ReportActionLogEntryResponse[]>();
  for (const entry of actions) {
    const key = groupKeyFor(entry);
    if (!key) continue;
    const existing = membersByKey.get(key);
    if (existing) existing.push(entry);
    else membersByKey.set(key, [entry]);
  }

  const items: TimelineItem[] = [];
  const emitted = new Set<string>();
  for (const entry of actions) {
    const key = groupKeyFor(entry);
    if (!key) {
      items.push({ kind: "single", entry, anchor: entry.timestamp });
      continue;
    }
    if (emitted.has(key)) continue;
    emitted.add(key);
    const members = membersByKey.get(key) ?? [entry];
    const timestamps = members.map((m) => m.timestamp);
    const start = timestamps.reduce((a, b) => (a < b ? a : b));
    const end = timestamps.reduce((a, b) => (a > b ? a : b));
    const meta = entry.metadata;
    const integrationType =
      meta && typeof meta.integration_type === "string"
        ? meta.integration_type
        : "";
    items.push({
      kind: "group",
      key,
      actionType: entry.action_type,
      integrationType,
      entries: members,
      start,
      end,
      anchor: start,
    });
  }

  items.sort((a, b) =>
    a.anchor < b.anchor ? -1 : a.anchor > b.anchor ? 1 : 0
  );
  return items;
};

/** The task id (job_ref_id) used to find a task's log lines, if present. */
export const taskLogId = (
  metadata: Record<string, unknown> | null
): string | null =>
  metadata && typeof metadata.job_ref_id === "string"
    ? metadata.job_ref_id
    : null;

/** GCP Logs Explorer URL filtered to a single task's logs over the last day. */
export const taskLogsUrl = (taskId: string): string =>
  `https://console.cloud.google.com/logs/query;query=${encodeURIComponent(
    taskId
  )};duration=P1D`;

/** Failure detail (error code + description) for an expanded task row, or "". */
export const formatTaskError = (
  metadata: Record<string, unknown> | null
): string => {
  if (!metadata) return "";
  const parts: string[] = [];
  if (typeof metadata.error_code === "string") {
    parts.push(`Error: ${metadata.error_code}`);
  }
  if (typeof metadata.error_description === "string") {
    parts.push(metadata.error_description);
  }
  return parts.join(" — ");
};
